import React, { useRef, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { IconMinus, IconPlus, IconSkull } from '@tabler/icons-react';
import Button from 'components/button/Button';
import { getGradientByIndexInverted } from 'stores/appStore';
import { CommanderDamage } from './CommanderCardsGrid';
import styles from './CommanderCard.css';

export interface CommanderCardProps {
    data: CommanderDamage;
    onIncrease: (opponentId: string, amount: number) => void;
    onDecrease: (opponentId: string, amount: number) => void;
    onIncreaseDeaths?: () => void;
    onDecreaseDeaths?: () => void;
    isBig?: boolean;
    commanderDeaths?: number;
    cardIndex?: number;
}

const CommanderCard = observer(({
    data,
    onIncrease,
    onDecrease,
    onIncreaseDeaths,
    onDecreaseDeaths,
    isBig = false,
    commanderDeaths = 0,
    cardIndex = 0
}: CommanderCardProps) => {
    const gradientColors = getGradientByIndexInverted(cardIndex);
    const [isShaking, setIsShaking] = useState(false);
    const prevDeathsRef = useRef(commanderDeaths);
    const leftSideOverlayRef = useRef<HTMLDivElement>(null);
    const rightSideOverlayRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // Only shake if deaths actually changed (not just on mount/modal open)
        if (prevDeathsRef.current !== commanderDeaths && data.isMe) {
            setIsShaking(true);
            const timer = setTimeout(() => setIsShaking(false), 250);
            prevDeathsRef.current = commanderDeaths;
            return () => clearTimeout(timer);
        }
        prevDeathsRef.current = commanderDeaths;
    }, [commanderDeaths, data.isMe]);

    const animateLeftFeedback = () => {
        if (leftSideOverlayRef.current) {
            leftSideOverlayRef.current.style.opacity = '1';
            setTimeout(() => {
                if (leftSideOverlayRef.current) {
                    leftSideOverlayRef.current.style.opacity = '0';
                }
            }, 150);
        }
    };

    const animateRightFeedback = () => {
        if (rightSideOverlayRef.current) {
            rightSideOverlayRef.current.style.opacity = '1';
            setTimeout(() => {
                if (rightSideOverlayRef.current) {
                    rightSideOverlayRef.current.style.opacity = '0';
                }
            }, 150);
        }
    };

    const onPressDecrease = () => {
        animateLeftFeedback();
        if (data.isMe) {
            onDecreaseDeaths?.();
        } else {
            onDecrease(data.opponentId!, 1);
        }
    };

    const onLongPressDecrease = () => {
        animateLeftFeedback();
        if (!data.isMe) {
            onDecrease(data.opponentId!, 10);
        }
    };

    const onPressIncrease = () => {
        animateRightFeedback();
        if (data.isMe) {
            onIncreaseDeaths?.();
        } else {
            onIncrease(data.opponentId!, 1);
        }
    };

    const onLongPressIncrease = () => {
        animateRightFeedback();
        if (!data.isMe) {
            onIncrease(data.opponentId!, 10);
        }
    };

    const gradientStyle = {
        background: `linear-gradient(135deg, ${gradientColors[0]}, ${gradientColors[1]})`,
    };

    if (!isBig) {
        return (
            <div className={styles.card}>
                <div className={styles.gradientBackground} style={gradientStyle} />
                {data.isMe ? (
                    <div className={styles.deathCountContainer}>
                        <IconSkull 
                            size={14} 
                            className={`${styles.skullIcon} ${isShaking ? styles.shaking : ''}`}
                        />
                        <span className={styles.text}>
                            {commanderDeaths ? commanderDeaths : ''}
                        </span>
                    </div>
                ) : (
                    <span className={styles.text}>{data.damage}</span>
                )}
            </div>
        );
    }

    return (
        <div className={`${styles.card} ${styles.cardBig}`}>
            <div className={`${styles.gradientBackground} ${styles.gradientBig}`} style={gradientStyle} />
            
            <Button 
                className={styles.leftSide}
                activeOpacity={1}
                onPress={onPressDecrease}
                onLongPress={onLongPressDecrease}
            >
                <div 
                    className={styles.sideOverlay} 
                    ref={leftSideOverlayRef}
                >
                    <div className={styles.leftGradient} />
                </div>
                <IconMinus size={50} className={`${styles.sideIcon} ${styles.sideIconLeft}`} />
            </Button>

            <Button 
                className={styles.rightSide}
                activeOpacity={1}
                onPress={onPressIncrease}
                onLongPress={onLongPressIncrease}
            >
                <div 
                    className={styles.sideOverlay} 
                    ref={rightSideOverlayRef}
                >
                    <div className={styles.rightGradient} />
                </div>
                <IconPlus size={50} className={`${styles.sideIcon} ${styles.sideIconRight}`} />
            </Button>

            <div className={styles.centerContainer}>
                {data.isMe ? (
                    <div className={styles.deathCountContainerBig}>
                        <IconSkull 
                            size={35}
                            className={`${styles.skullIconBig} ${isShaking ? styles.shaking : ''}`}
                        />
                        <span className={`${styles.text} ${styles.textBig}`}>
                            {commanderDeaths ? commanderDeaths : ''}
                        </span>
                    </div>
                ) : (
                    <span className={`${styles.text} ${styles.textBig}`}>{data.damage}</span>
                )}
            </div>
        </div>
    );
});

export default CommanderCard;
